import React, { useEffect } from "react";
import { CheckCircle2, ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

/**
 * ImportSuccess — Step 5 (final).
 * Shown after successful import. Displays counts and CTAs to continue.
 */
export default function ImportSuccess({ result, onReset }) {
  const inserted = result?.inserted || 0;
  const skipped = result?.skipped || 0;
  const replaced = result?.replaced || 0;
  const merged = result?.merged || 0;
  const total = inserted + replaced + merged;

  // Optional: animate counter (simple fade)
  useEffect(() => {}, [inserted]);

  return (
    <div
      className="rounded-3xl border border-success/20 bg-gradient-to-br from-success/[0.08] to-transparent p-6 md:p-10 text-center animate-fade-in"
      data-testid="import-step-success"
    >
      <div className="mx-auto h-16 w-16 rounded-3xl bg-success/15 border border-success/30 text-success flex items-center justify-center mb-4 animate-in zoom-in-50 duration-300">
        <CheckCircle2 className="h-8 w-8" />
      </div>
      <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
        {total} {total > 1 ? tfr("joueurs ajoutés à votre pipeline") : tfr("joueur ajouté à votre pipeline")}
      </h2>
      <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
        {tfr("Vos données ont été importées avec succès. Vous pouvez maintenant les retrouver dans votre Pipeline.")}
      </p>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 max-w-2xl mx-auto">
        <Stat label="Nouveaux" value={inserted} tone="success" />
        <Stat label={tfr("Remplacés")} value={replaced} tone="info" />
        <Stat label={tfr("Fusionnés")} value={merged} tone="info" />
        <Stat label={tfr("Ignorés")} value={skipped} tone="muted" />
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
        <Link to="/pipeline">
          <Button
            size="lg"
            className="h-11 px-5 bg-primary text-primary-foreground hover:bg-primary/90 gap-2"
            data-testid="import-success-pipeline"
          >
            Voir mes joueurs <ArrowRight className="h-4 w-4" />
          </Button>
        </Link>
        <Button
          size="lg"
          variant="outline"
          onClick={onReset}
          className="h-11 px-5 border-border dark:border-border hover:bg-accent dark:hover:bg-secondary/60 gap-2"
          data-testid="import-success-again"
        >
          <Plus className="h-4 w-4" /> Importer d'autres joueurs
        </Button>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = {
    success: "text-success",
    info: "text-info",
    muted: "text-muted-foreground",
  }[tone];
  return (
    <div className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/50 p-4">
      <div className={cn("text-2xl font-semibold tabular-nums tracking-tight", toneCls)}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{label}</div>
    </div>
  );
}
